import dayjs from "dayjs";

export const formatProviderName = (
  provider: {
    firstName?: string | null;
    lastName?: string | null;
    honorific?: string | null;
  },
  fullName?: string,
): string => {
  const name = (
    fullName ?? `${provider?.firstName || ""} ${provider?.lastName || ""}`
  ).trim();
  if (!name) return "";
  return provider?.honorific
    ? `${name}, ${provider.honorific.replace(/_/g, "-")}`
    : name;
};

export const getLabelFromValue = (
  value: any,
  list: any,
  labelKey = "name",
  defaultValue = "-",
) => {
  const item = list.find((item: any) => item.value === value);
  return item ? item[labelKey] : defaultValue;
};

export const handleOnlyNumbers = (event: any, setValue: any) => {
  const { name, value } = event.target;
  const numericValue = value.replace(/[^0-9]/g, "");
  setValue(name, numericValue, { shouldValidate: true });
};

export function capitalizeWord(str) {
  if (typeof str !== "string" || !str.trim()) {
    return "-";
  }

  const word = str?.trim()?.toLowerCase();
  return word?.charAt(0)?.toUpperCase() + word?.slice(1);
}

export const calculateAge = (dateOfBirth: string): number => {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

interface ParsedCalendarDate {
  year: number;
  month: number; // 1-indexed (1-12)
  day: number;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month, 0).getDate();
};

// Rejects impossible dates such as 2020-02-30 or 2020-13-01.
const toCalendarDate = (
  year: number,
  month: number,
  day: number,
): ParsedCalendarDate | null => {
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > getDaysInMonth(year, month)) return null;
  return { year, month, day };
};

const fromLocalDate = (d: Date): ParsedCalendarDate | null => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  return toCalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
};

const parseCalendarDateInput = (
  input?: string | Date | null,
  { useLocalTime = false }: { useLocalTime?: boolean } = {},
): ParsedCalendarDate | null => {
  if (!input) return null;

  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return null;

    // MM/DD/YYYY or M/D/YYYY
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
      return toCalendarDate(
        parseInt(slashMatch[3], 10),
        parseInt(slashMatch[1], 10),
        parseInt(slashMatch[2], 10),
      );
    }

    // YYYY-MM-DD (with optional time / ISO timezone)
    const dashMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (dashMatch) {
      const hasTime = trimmed.length > dashMatch[0].length;
      if (useLocalTime && hasTime) {
        return fromLocalDate(new Date(trimmed));
      }
      return toCalendarDate(
        parseInt(dashMatch[1], 10),
        parseInt(dashMatch[2], 10),
        parseInt(dashMatch[3], 10),
      );
    }

    return fromLocalDate(new Date(trimmed));
  }

  if (input instanceof Date) {
    return fromLocalDate(input);
  }

  if (typeof (input as any)?.toDate === "function") {
    return fromLocalDate((input as any).toDate());
  }

  return null;
};

const getMonthlyAnniversary = (
  dobDay: number,
  targetYear: number,
  targetMonth: number,
): ParsedCalendarDate => {
  // A month is counted on the same day of the month as the patient's birth.
  // If that day doesn't exist in a month, the month is counted on the last day of that month
  // (this also covers Feb 29 birthdays, which are counted on Feb 28 in non-leap years).
  const day = Math.min(dobDay, getDaysInMonth(targetYear, targetMonth));

  return { year: targetYear, month: targetMonth, day };
};

const dateDiffInDays = (
  d1: ParsedCalendarDate,
  d2: ParsedCalendarDate,
): number => {
  const utc1 = Date.UTC(d1.year, d1.month - 1, d1.day);
  const utc2 = Date.UTC(d2.year, d2.month - 1, d2.day);
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((utc2 - utc1) / msPerDay);
};

export const calculatePatientAge = (
  dateOfBirth?: string | Date | null,
  asOfDate?: string | Date | null,
): string => {
  const dob = parseCalendarDateInput(dateOfBirth);
  if (!dob) {
    return "N/A";
  }

  const asOf = parseCalendarDateInput(asOfDate || new Date(), {
    useLocalTime: true,
  });

  if (!asOf || dateDiffInDays(dob, asOf) < 0) {
    return "N/A";
  }

  let totalMonths = (asOf.year - dob.year) * 12 + (asOf.month - dob.month);

  const anniversaryThisMonth = getMonthlyAnniversary(
    dob.day,
    asOf.year,
    asOf.month,
  );

  let lastAnniversary: ParsedCalendarDate;
  if (asOf.day < anniversaryThisMonth.day) {
    totalMonths -= 1;
    let prevYear = asOf.year;
    let prevMonth = asOf.month - 1;
    if (prevMonth === 0) {
      prevMonth = 12;
      prevYear -= 1;
    }
    lastAnniversary = getMonthlyAnniversary(dob.day, prevYear, prevMonth);
  } else {
    lastAnniversary = anniversaryThisMonth;
  }

  const days = dateDiffInDays(lastAnniversary, asOf);

  // Band 1: Birth to under 1 month
  if (totalMonths < 1) {
    const totalDays = dateDiffInDays(dob, asOf);
    return `${totalDays}d`;
  }

  // Band 2: 1 month to under 1 year
  if (totalMonths < 12) {
    return `${totalMonths}mo ${days}d`;
  }

  // Band 3: 1 year and older
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  return `${years}y ${remainingMonths}mo`;
};

/**
 * Formats date of birth alongside age:
 * e.g., "10/10/2016 (10y 1mo)" or "N/A"
 */
export const formatCalendarDate = (date?: string | Date | null): string => {
  if (!date) return "";

  const parsed = parseCalendarDateInput(date);
  if (!parsed) return "N/A";

  return `${pad2(parsed.month)}/${pad2(parsed.day)}/${parsed.year}`;
};

export const formatDateWithAge = (
  dateOfBirth?: string | Date | null,
  asOfDate?: string | Date | null,
): string => {
  if (!dateOfBirth) return "";

  const formattedDate = formatCalendarDate(dateOfBirth);
  if (formattedDate === "N/A") return "N/A";

  const age = calculatePatientAge(dateOfBirth, asOfDate);

  if (age === "N/A") {
    return "N/A";
  }

  return `${formattedDate} (${age})`;
};

export const formatDateTime = (dateTime: string): string => {
  if (!dateTime) return "";

  const date = new Date(dateTime);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${day} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
};

export const formatDateLocal = (dateTime: string): string => {
  if (!dateTime) return "";

  const date = new Date(dateTime + "Z");
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${day} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
};

export const formatPhoneNumber = (input: string | null | undefined): string => {
  if (typeof input !== "string") return "-";

  const cleaned = input.replace(/\D/g, "");
  if (cleaned.length === 0) return "";
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6)
    return `(${cleaned.slice(0, 3)})-${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)})-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

export const formatTo12Hour = (time) => {
  const [hourStr, minute] = time?.split(":");
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12; // converts 0 to 12
  const pad = (n) => String(n)?.padStart(2, "0");
  return `${pad(hour)}:${pad(minute)} ${ampm}`;
};

export const formatTime = (time) => {
  const [hour, minute] = time.split(":").map(Number);
  const second = 0;
  const pad = (n) => String(n)?.padStart(2, "0");
  return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
};

export const formatEnums = (status) => {
  return status
    .toLowerCase() // Convert to lowercase
    .split(/[_-\s]+/) // Split by _, -, or spaces
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(" "); // Join with space
};

/**
 * A charge's service date as "Oct 2026". The calendar date is read off the
 * string itself, so a UTC midnight timestamp cannot roll back into the
 * previous month in a US timezone.
 */
export const formatServiceMonth = (serviceDate?: string) => {
  if (!serviceDate) return "-";
  const datePart = /^\d{4}-\d{2}-\d{2}/.exec(serviceDate)?.[0];
  const parsed = dayjs(datePart ?? serviceDate);
  return parsed.isValid() ? parsed.format("MMM YYYY") : "-";
};
